import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  VStack,
  HStack,
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Text,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  Icon,
  FormLabel,
  Grid,
  GridItem,
  useToast,
  Alert,
  AlertIcon,
  Flex,
  Spinner,
  Tooltip,
  IconButton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Input,
  Switch,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorMode,
  useColorModeValue,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
} from "@chakra-ui/react";
import { projectTaskAPI } from "../../services/api";
import {
  FiPlus,
  FiMinus,
  FiEdit2,
  FiCalendar,
  FiTrendingUp,
  FiActivity,
  FiClock,
  FiEye,
  FiBarChart2,
  FiTarget,
  FiX,
  FiDownload,
  FiRefreshCw,
  FiSettings,
  FiSearch,
  FiFilter,
  FiMoreVertical,
  FiDollarSign,
  FiTrendingDown,
  FiSun,
  FiMoon,
  FiAlertCircle,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import WeeklyProgressForm from "./WeeklyProgressForm";
import { useProjectPermission } from "../../hooks/useProjectPermission";
// ✅ Get user's role

const ProgressTracker = ({
  projectTasks,
  onProgressUpdate,
  projectId,
  currentProject,
  budgetSummary = {},
}) => {
  const { isAdmin, isProjectManager, isSiteEngineer } = useProjectPermission();

  // ✅ Check if user can see reports
  const canViewReports = isAdmin || isProjectManager;

  // Dark theme support
  const { colorMode, toggleColorMode } = useColorMode();

  // Color mode values at component level
  const bgPrimary = useColorModeValue("white", "gray.800");
  const bgSecondary = useColorModeValue("gray.50", "gray.700");
  const bgTertiary = useColorModeValue("gray.100", "gray.600");
  const textPrimary = useColorModeValue("gray.800", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.300");
  const textMuted = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const shadowColor = useColorModeValue("rgba(0,0,0,0.1)", "rgba(0,0,0,0.3)");

  const gradientBg = useColorModeValue(
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)"
  );

  const cardGradient = useColorModeValue(
    "linear-gradient(145deg, #ffffff 0%, #f7fafc 100%)",
    "linear-gradient(145deg, #1a202c 0%, #2d3748 100%)"
  );

  const grayBg = useColorModeValue("gray.100", "gray.800");
  const greenBg = useColorModeValue("green.50", "green.900");
  const yellowBg = useColorModeValue("yellow.50", "yellow.900");
  const redBg = useColorModeValue("red.50", "red.900");
  const overlayBg = useColorModeValue("blackAlpha.600", "blackAlpha.800");
  const modalBorderColor = useColorModeValue(
    "rgba(0,0,0,0.1)",
    "rgba(255,255,255,0.1)"
  );
  const modalCloseBg = useColorModeValue(
    "rgba(0,0,0,0.1)",
    "rgba(255,255,255,0.1)"
  );
  const modalCloseHoverBg = useColorModeValue(
    "rgba(0,0,0,0.2)",
    "rgba(255,255,255,0.2)"
  );
  const modalCloseActiveBg = useColorModeValue(
    "rgba(0,0,0,0.3)",
    "rgba(255,255,255,0.3)"
  );
  const scrollTrackBg = useColorModeValue(
    "rgba(0,0,0,0.05)",
    "rgba(255,255,255,0.05)"
  );
  const scrollThumbBg = useColorModeValue(
    "rgba(66, 153, 225, 0.6)",
    "rgba(66, 153, 225, 0.6)"
  );
  const scrollThumbHoverBg = useColorModeValue(
    "rgba(66, 153, 225, 0.8)",
    "rgba(66, 153, 225, 0.8)"
  );

  // State Management
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
  });
  const taskRowRefs = useRef({});

  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedProgress, setSelectedProgress] = useState(null);
  const [progressHistory, setProgressHistory] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);
  const [showCostDetails, setShowCostDetails] = useState(false);

  useEffect(() => {
    const fetchAllProgressHistories = async () => {
      if (!projectTasks || projectTasks.length === 0) return;

      const historyPromises = projectTasks.map((task) =>
        projectTaskAPI
          .getTaskProgressHistory(task.id)
          .then((response) => ({
            taskId: task.id,
            data: response.data.success ? response.data.data : [],
          }))
          .catch((error) => {
            console.error(`Error fetching history for task ${task.id}:`, error);
            return { taskId: task.id, data: [] };
          })
      );

      const results = await Promise.all(historyPromises);

      const historyMap = results.reduce((acc, { taskId, data }) => {
        acc[taskId] = Array.isArray(data) ? data : [];
        return acc;
      }, {});

      setProgressHistory(historyMap);
    };

    fetchAllProgressHistories();
  }, [projectTasks]);
  // ============ END OF NEW CODE ============
  // Weekly Progress Modal
  const {
    isOpen: isWeeklyProgressOpen,
    onOpen: onWeeklyProgressOpen,
    onClose: onWeeklyProgressClose,
  } = useDisclosure();

  // History Modal
  const {
    isOpen: isHistoryOpen,
    onOpen: onHistoryOpen,
    onClose: onHistoryClose,
  } = useDisclosure();

  const toast = useToast();

  // Enhanced currency detection with multiple fallbacks
  const getProjectCurrency = () => {
    console.log("Currency Debug:", {
      budgetSummary,
      "budgetSummary.currency": budgetSummary?.currency,
      currentProject,
      "currentProject.currency": currentProject?.currency,
      "currentProject.projectCurrency": currentProject?.projectCurrency,
    });

    const currency =
      budgetSummary?.currency ||
      currentProject?.currency ||
      currentProject?.projectCurrency ||
      currentProject?.details?.currency ||
      (projectTasks.length > 0 ? projectTasks[0]?.currency : "AED") ||
      "AED";

    console.log("Selected currency:", currency);
    return currency;
  };

  const projectCurrency = getProjectCurrency();

  // Utility Functions
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num || 0);
  };

  const formatCurrency = (amount, currency = projectCurrency) => {
    const projectCurr = currency || projectCurrency || "AED";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: projectCurr,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDetailedCurrency = (amount, currency = projectCurrency) => {
    const projectCurr = currency || projectCurrency || "AED";

    if (amount >= 1000000) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: projectCurr,
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
        notation: "compact",
        compactDisplay: "short",
      }).format(amount);
    }

    return formatCurrency(amount, projectCurr);
  };

  const formatCompactCurrency = (amount, currency = projectCurrency) => {
    const projectCurr = currency || projectCurrency || "AED";

    if (amount >= 1000000) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: projectCurr,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        // notation: 'compact',
        // compactDisplay: 'short'
      }).format(amount);
    } else if (amount >= 1000) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: projectCurr,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        // notation: 'compact',
        // compactDisplay: 'short'
      }).format(amount);
    }
    return formatCurrency(amount, projectCurr);
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split("-");
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
  };

  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = -6; i <= 5; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;
      const label = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });
      options.push({ value, label });
    }
    return options;
  };

  // Cost Calculation Functions
  const calculateTaskCosts = (task) => {
    const budgetedCost = task.budgetedCost || 0;
    const consumedCost = task.consumedCost || 0;
    const remainingCost = task.remainingCost || budgetedCost - consumedCost;

    const quantity = task.quantity || 0;
    const installedQuantity = task.totalInstalledQuantity || 0;
    const progressPercentage = quantity > 0 ? installedQuantity / quantity : 0;

    const earnedValue = budgetedCost * progressPercentage;
    const costVariance = earnedValue - consumedCost;
    const costPerformanceIndex =
      consumedCost > 0 ? earnedValue / consumedCost : 1;
    const costEfficiency =
      consumedCost > 0 ? (earnedValue / consumedCost) * 100 : 100;
    const projectedTotalCost =
      costPerformanceIndex > 0
        ? budgetedCost / costPerformanceIndex
        : budgetedCost;
    const projectedOverrun = projectedTotalCost - budgetedCost;

    let riskLevel = "L";
    if (costPerformanceIndex < 0.8) {
      riskLevel = "H";
    } else if (costPerformanceIndex < 0.9) {
      riskLevel = "M";
    }

    return {
      budgetedCost,
      consumedCost,
      remainingCost,
      earnedValue,
      costVariance,
      costPerformanceIndex,
      costEfficiency,
      projectedTotalCost,
      projectedOverrun,
      isOverBudget: costPerformanceIndex < 1,
      isUnderBudget: costPerformanceIndex > 1.1,
      riskLevel,
      progressPercentage: progressPercentage * 100,
      quantity,
      installedQuantity,
    };
  };

  // Enhanced Task-Level CPI Calculation
  const calculateCPI = (task) => {
    const costs = calculateTaskCosts(task);
    const plannedManhours = task.totalBudgetedManhours || 0;
    const actualManhours = task.totalConsumedManhours || 0;

    const progressPercentage =
      task.quantity > 0
        ? ((task.totalInstalledQuantity || 0) / task.quantity) * 100
        : 0;

    if (actualManhours === 0) {
      return {
        cpi: null,
        earnedValue: 0,
        actualCost: 0,
        interpretation: "No Progress",
        colorScheme: "gray",
        costs,
      };
    }

    if (plannedManhours === 0) {
      return {
        cpi: null,
        earnedValue: 0,
        actualCost: actualManhours,
        interpretation: "No Plan",
        colorScheme: "gray",
        costs,
      };
    }

    const earnedValue =
      plannedManhours * (Math.min(progressPercentage, 100) / 100);
    const actualCost = actualManhours;
    const earnedManhours = task.totalInstalledQuantity * task.productivity;
    // const cpi = earnedValue / actualCost;
    const cpi = earnedManhours / task.totalConsumedManhours;

    let interpretation;
    let colorScheme;
    if (cpi >= 1.0) {
      interpretation = "Under Budget";
      colorScheme = "green";
    } else if (cpi >= 0.9) {
      interpretation = "Near Budget";
      colorScheme = "yellow";
    } else {
      interpretation = "Over Budget";
      colorScheme = "red";
    }

    return {
      cpi: cpi,
      earnedValue: earnedValue,
      actualCost: actualCost,
      interpretation: interpretation,
      colorScheme: colorScheme,
      costs,
    };
  };

  // Project-Level Cost Calculations
  const calculateProjectCosts = () => {
    let totalBudgetedCost = 0;
    let totalConsumedCost = 0;
    let totalEarnedValue = 0;
    let totalRemainingCost = 0;
    let totalPlannedManhours = 0;
    let totalConsumedManhours = 0;
    let startedTotalPlannedManhours = 0;
    let startedTotalConsumedManhours = 0;
    let startedTotalInstalledQuantity = 0;
    let startedTotalQuantity = 0;
    let totalEarnedManhours = 0;

    projectTasks.forEach((task) => {
      const costs = calculateTaskCosts(task);

      totalBudgetedCost += costs.budgetedCost;
      totalConsumedCost += costs.consumedCost;
      totalEarnedValue += costs.earnedValue;
      totalRemainingCost += costs.remainingCost;
      totalPlannedManhours += task.totalBudgetedManhours || 0;
      totalConsumedManhours += task.totalConsumedManhours || 0;
      totalEarnedManhours +=
        (task.totalInstalledQuantity || 0) * (task.productivity || 0);
      // console.log('total earned mh',totalEarnedManhours);
      if (
        task.totalConsumedManhours > 0 &&
        (task.totalInstalledQuantity || 0) > 0
      ) {
        startedTotalPlannedManhours += task.totalBudgetedManhours || 0;
        startedTotalConsumedManhours += task.totalConsumedManhours || 0;
        startedTotalInstalledQuantity += task.totalInstalledQuantity || 0;
        startedTotalQuantity += task.quantity || 0;
      }
    });

    // ✅ CORRECTED: Calculate project progress based on quantity/installation
    // const projectProgress = totalBudgetedCost > 0 ? (totalEarnedValue / totalBudgetedCost) * 100 : 0;
    // const projectProgress = totalBudgetedCost > 0 ? (totalEarnedValue / totalBudgetedCost) * 100 : 0;
    const projectProgress =
      totalPlannedManhours > 0 ? totalEarnedManhours / totalPlannedManhours : 0;

    // ✅ CORRECTED: Calculate CPI using MAN-HOURS (as per client requirement)
    // CPI = EV / AC
    // EV = (Planned Man-hours) × (% Progress)
    // AC = Actual Man-hours consumed
    const totalInstalledQuantity = projectTasks.reduce(
      (sum, task) => sum + (task.totalInstalledQuantity || 0),
      0
    );
    const totalQuantity = projectTasks.reduce(
      (sum, task) => sum + (task.quantity || 0),
      0
    );

    // ✅ CORRECTED: Calculate progress based on installed quantity vs total quantity
    const quantityProgress =
      totalQuantity > 0 ? (totalInstalledQuantity / totalQuantity) * 100 : 0;

    // ✅ CORRECTED: Calculate EV using quantity-based progress
    const earnedValueManhours = totalPlannedManhours * (quantityProgress / 100);

    // ✅ CORRECTED: Calculate CPI using man-hours
    // const projectCPI = totalConsumedManhours > 0
    //   ? parseFloat((earnedValueManhours / totalConsumedManhours).toFixed(2))
    //   : 1.00;

    console.log("startedTotalPlannedManhours", startedTotalPlannedManhours);
    console.log("startedTotalConsumedManhours", startedTotalConsumedManhours);
    console.log("startedTotalInstalledQuantity", startedTotalInstalledQuantity);
    console.log("startedTotalQuantity", startedTotalQuantity);

    const startedEv =
      startedTotalConsumedManhours > 0
        ? (startedTotalInstalledQuantity / startedTotalQuantity) *
          startedTotalPlannedManhours
        : 0;

    // const projectCPI = startedTotalConsumedManhours > 0 ?
    //   parseFloat((startedEv / startedTotalConsumedManhours).toFixed(2))
    //   : 1.00;
    // Fixed: Return 0 when no work has started (avoid NaN from 0/0)
    const projectCPI = totalConsumedManhours > 0 
      ? totalEarnedManhours / totalConsumedManhours 
      : 0;

    console.log("totalPlannedManhours", totalPlannedManhours);
    console.log("totalConsumedManhours", totalConsumedManhours);
    console.log("earnedValueManhours", earnedValueManhours);
    console.log("projectCPI", projectCPI);

    // Cost variance still uses cost-based values
    const projectCostVariance = totalEarnedValue - totalConsumedCost;

    const projectedTotalCost =
      projectCPI > 0 ? totalBudgetedCost / projectCPI : totalBudgetedCost;

    return {
      totalBudgetedCost,
      totalConsumedCost,
      totalEarnedValue,
      totalRemainingCost,
      totalPlannedManhours,
      totalConsumedManhours,
      projectCostVariance,
      projectCPI: parseFloat(projectCPI.toFixed(2)), // ✅ Now based on man-hours
      projectProgress: Math.min(projectProgress, 100),
      projectedTotalCost,
      projectedOverrun: projectedTotalCost - totalBudgetedCost,
    };
  };

  // NEW: Calculate category-level summaries
  const calculateCategorySummary = (tasks) => {
    let totalBudgetedManhours = 0;
    let totalConsumedManhours = 0;
    let totalBudgetedCost = 0;
    let totalConsumedCost = 0;
    let totalEarnedManhours = 0;

    tasks.forEach((task) => {
      // console.log('task for category summary', task);
      // console.log('installed quantity', task.installedQuantity);
      // console.log('productivity', task.productivity);
      totalBudgetedManhours += task.totalBudgetedManhours || 0;
      totalConsumedManhours += task.totalConsumedManhours || 0;
      totalBudgetedCost += task.budgetedCost || 0;
      totalConsumedCost += task.consumedCost || 0;
      totalEarnedManhours +=
        (task.totalInstalledQuantity || 0) * task.productivity;
    });
    const costConstant = totalBudgetedCost / totalBudgetedManhours || 0;
    // console.log(costConstant);
    // const lapsedManhours = summary.totalEarnedManhours - summary.totalConsumed;
    // const costVariance = lapsedManhours * costConstant;

    const lapsedManhours = totalEarnedManhours - totalConsumedManhours;

    const totalEarnedCost = totalEarnedManhours * costConstant;
    const costVariance = lapsedManhours * costConstant;
    console.log("earned manhours", totalEarnedManhours);

    return {
      totalBudgetedManhours,
      totalConsumedManhours,
      lapsedManhours,
      totalBudgetedCost,
      totalEarnedCost,
      totalConsumedCost,
      costVariance,
      totalEarnedManhours,
    };
  };

  const getProjectCostStatus = (projectCPI) => {
    if (projectCPI === null || projectCPI === 0) {
      return { color: "gray", text: "No Data", bgColor: grayBg };
    }
    if (projectCPI >= 1.0) {
      return { color: "green", text: "Under Budget", bgColor: greenBg };
    } else if (projectCPI >= 0.9) {
      return { color: "yellow", text: "Near Budget", bgColor: yellowBg };
    } else {
      return { color: "red", text: "Over Budget", bgColor: redBg };
    }
  };

  // Enhanced CPI Badge Component
  const CPIBadge = ({ task }) => {
    const cpiData = calculateCPI(task);

    if (cpiData.cpi === null) {
      return (
        <Tooltip label={cpiData.interpretation}>
          <Badge colorScheme="gray" fontSize="10">
            N/A
          </Badge>
        </Tooltip>
      );
    }

    const cpiFormatted = cpiData.cpi.toFixed(2);

    return (
      <Tooltip
        label={
          <VStack spacing={1} align="start">
            <Text fontSize="xs" fontWeight="bold">
              CPI & Cost Breakdown
            </Text>
            <Text fontSize="xs">
              Earned Value: {cpiData.earnedValue.toFixed(2)} hrs
            </Text>
            <Text fontSize="xs">
              Actual Cost: {cpiData.actualCost.toFixed(2)} hrs
            </Text>
            <Text fontSize="xs">CPI: {cpiFormatted}</Text>
            <Divider />
            <Text fontSize="xs">
              Budget Cost: {formatCurrency(cpiData.costs.budgetedCost)}
            </Text>
            <Text fontSize="xs">
              Consumed Cost: {formatCurrency(cpiData.costs.consumedCost)}
            </Text>
            <Text fontSize="xs">
              Cost Variance: {formatCurrency(cpiData.costs.costVariance)}
            </Text>
            <Text fontSize="xs" color="gray.300">
              Currency: {projectCurrency}
            </Text>
            <Text
              fontSize="xs"
              fontWeight="bold"
              color={
                cpiData.colorScheme === "green"
                  ? "green.200"
                  : cpiData.colorScheme === "yellow"
                  ? "yellow.200"
                  : "red.200"
              }
            >
              {cpiData.interpretation}
            </Text>
          </VStack>
        }
        placement="top"
        hasArrow
      >
        <Badge colorScheme={cpiData.colorScheme} fontSize="xs" cursor="pointer">
          {cpiFormatted}
        </Badge>
      </Tooltip>
    );
  };

  // Cost Badge Component
  const CostBadge = ({ cost, variance = null, isCompact = false }) => {
    if (isCompact) {
      return (
        <Text fontSize="xs" fontWeight="medium" color={textPrimary}>
          {formatCompactCurrency(cost)}
        </Text>
      );
    }

    let colorScheme = "blue";
    if (variance !== null) {
      if (variance > 0) colorScheme = "green";
      else if (variance < -1000) colorScheme = "red";
      else if (variance < 0) colorScheme = "orange";
    }

    return (
      <Badge colorScheme={colorScheme} fontSize="xs" px={2} py={1}>
        {formatCompactCurrency(cost)}
      </Badge>
    );
  };

  // Fetch task progress history
  const fetchTaskHistory = async (taskId) => {
    const existingHistory = progressHistory[taskId];
    if (existingHistory && Array.isArray(existingHistory)) {
      return existingHistory;
    }

    setLoadingHistory(true);
    try {
      const response = await projectTaskAPI.getTaskProgressHistory(taskId);
      if (response.data.success) {
        const historyData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setProgressHistory((prev) => ({
          ...prev,
          [taskId]: historyData,
        }));
        return historyData;
      }
    } catch (error) {
      console.error("Error fetching task history:", error);
    } finally {
      setLoadingHistory(false);
    }
    return [];
  };
  // Add this helper function in ProgressTracker.jsx to check if target qty was updated
  // Update your helper function to accept taskId instead
  const hasRecentTargetUpdate = (taskId) => {
    const taskHistory = progressHistory[taskId];

    if (
      !taskHistory ||
      !Array.isArray(taskHistory) ||
      taskHistory.length === 0
    ) {
      return false;
    }

    // Check the most recent progress entry
    const latestProgress = taskHistory[0];

    // console.log("Latest progress entry:", latestProgress);

    // First, check if there's a targetedQuantity at the parent level
    if (
      latestProgress.targetedQuantity &&
      parseFloat(latestProgress.targetedQuantity) > 0
    ) {
      return true;
    }

    // Also check weeklyBreakdown if it exists
    if (latestProgress.weeklyBreakdown) {
      try {
        let breakdown = latestProgress.weeklyBreakdown;
        if (typeof breakdown === "string") {
          breakdown = JSON.parse(breakdown);
        }

        // Check if any week has a targetedQty value
        if (Array.isArray(breakdown)) {
          return breakdown.some(
            (week) => week.targetedQty && parseFloat(week.targetedQty) > 0
          );
        }
      } catch (error) {
        console.error("Error parsing weekly breakdown:", error);
      }
    }

    return false;
  };

  // Open Weekly Progress Form
  const openWeeklyProgressForm = async (task) => {
    setSelectedTask(task);

    const [year, month] = selectedMonth.split("-");
    const history = await fetchTaskHistory(task.id);
    const safeHistory = Array.isArray(history) ? history : [];
    const existingProgress = safeHistory.find(
      (p) => p.year === parseInt(year) && p.month === parseInt(month)
    );

    setSelectedProgress(existingProgress || null);
    onWeeklyProgressOpen();
  };

  // View Task History
  const viewTaskHistory = async (task) => {
    setSelectedTask(task);
    await fetchTaskHistory(task.id);
    onHistoryOpen();
  };

  // Handle Weekly Progress Save
  const handleWeeklyProgressSave = (savedData) => {
    const taskId = selectedTask?.id;

    // Store the scroll position BEFORE any state updates
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    // Close modal and update data
    onWeeklyProgressClose();
    onProgressUpdate();

    toast({
      title: "Success",
      description: "Weekly progress saved successfully",
      status: "success",
      duration: 3000,
    });

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (taskId && taskRowRefs.current[taskId]) {
          // First try to scroll to the specific task
          taskRowRefs.current[taskId]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        } else {
          // Fallback: restore the original scroll position
          window.scrollTo({
            top: scrollY,
            behavior: "smooth",
          });
        }
      }, 500); // Increased delay to wait for data refresh
    });
  };

  // Handler for toggling task expansion
  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  // Filter and sort tasks
  const filteredTasks = useMemo(() => {
    return projectTasks.filter((task) => {
      const query = searchQuery?.toLowerCase() || "";
      const matchesSearch =
        !searchQuery ||
        task.masterSubTask?.name
          ?.toLowerCase()
          .includes(query) ||
        task.masterSubTask?.code
          ?.toLowerCase()
          .includes(query) ||
        task.masterSubTask?.masterActivity?.name
          ?.toLowerCase()
          .includes(query) ||
        task.masterSubTask?.masterActivity?.code
          ?.toLowerCase()
          .includes(query) ||
        task.categoryName?.toLowerCase().includes(query);

      const matchesCategory =
        selectedCategory === "all" || task.categoryName === selectedCategory;

      const quantityProgress =
        task.quantity > 0
          ? ((task.totalInstalledQuantity || 0) / task.quantity) * 100
          : 0;
      const isCompleted = quantityProgress >= 100;
      const matchesCompletedFilter = showCompletedTasks || !isCompleted;

      return matchesSearch && matchesCategory && matchesCompletedFilter;
    });
  }, [projectTasks, searchQuery, selectedCategory, showCompletedTasks]);

  // Group tasks by category with proper ordering
  const tasksByCategory = useMemo(() => {
    const categoryMap = new Map();

    filteredTasks.forEach((task) => {
      const categoryName =
        task.projectTaskCategory?.name || task.categoryName || "Uncategorized";
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, {
          name: categoryName,
          order: task.projectTaskCategory?.order || 999,
          category: task.projectTaskCategory,
          tasks: [],
        });
      }
      categoryMap.get(categoryName).tasks.push(task);
    });

    const sortedCategories = Array.from(categoryMap.values()).sort((a, b) => {
      return (a.order || 0) - (b.order || 0);
    });

    const result = {};
    sortedCategories.forEach((categoryData) => {
      const sortedTasks = [...categoryData.tasks].sort((a, b) => {
        const activityOrderA = a.masterSubTask?.masterActivity?.order || 0;
        const activityOrderB = b.masterSubTask?.masterActivity?.order || 0;
        if (activityOrderA !== activityOrderB) {
          return activityOrderA - activityOrderB;
        }
        const subtaskOrderA = a.masterSubTask?.order || 0;
        const subtaskOrderB = b.masterSubTask?.order || 0;
        return subtaskOrderA - subtaskOrderB;
      });

      result[categoryData.name] = sortedTasks;
    });

    return result;
  }, [filteredTasks]);

  // Calculate project-level metrics
  const projectCosts = calculateProjectCosts();
  const projectStatus = getProjectCostStatus(projectCosts.projectCPI);

  // Export enhanced progress data
  const handleExportProgress = () => {
    const csvContent = generateProgressCSV();
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${
      currentProject?.name || "Project"
    }_Progress_Cost_Analysis_${projectCurrency}_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const generateProgressCSV = () => {
    let csv = `Project Progress & Cost Analysis Report\n`;
    csv += `Project: ${currentProject?.name || "Unnamed Project"}\n`;
    csv += `Currency: ${projectCurrency}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n`;
    csv += `Period: ${getMonthName(selectedMonth)}\n`;
    csv += `Theme: ${colorMode} mode\n\n`;

    csv += `PROJECT COST SUMMARY\n`;
    csv += `Overall Progress,${projectCosts.projectProgress.toFixed(1)}%\n`;
    csv += `Project CPI,${projectCosts.projectCPI?.toFixed(2) || "N/A"}\n`;
    csv += `Total Budget Cost (${projectCurrency}),${projectCosts.totalBudgetedCost.toFixed(
      2
    )}\n`;
    csv += `Total Consumed Cost (${projectCurrency}),${projectCosts.totalConsumedCost.toFixed(
      2
    )}\n`;
    csv += `Total Earned Value (${projectCurrency}),${projectCosts.totalEarnedValue.toFixed(
      2
    )}\n`;
    csv += `Cost Variance (${projectCurrency}),${projectCosts.projectCostVariance.toFixed(
      2
    )}\n`;
    csv += `Projected Total Cost (${projectCurrency}),${projectCosts.projectedTotalCost.toFixed(
      2
    )}\n`;
    csv += `Projected Overrun (${projectCurrency}),${projectCosts.projectedOverrun.toFixed(
      2
    )}\n\n`;

    csv += `DETAILED TASK ANALYSIS\n`;
    csv += `Task,Category,Progress %,CPI,Budget Cost (${projectCurrency}),Consumed Cost (${projectCurrency}),Earned Value (${projectCurrency}),Cost Variance (${projectCurrency}),Remaining Cost (${projectCurrency}),Risk Level\n`;

    filteredTasks.forEach((task) => {
      const quantityProgress =
        task.quantity > 0
          ? ((task.totalInstalledQuantity || 0) / task.quantity) * 100
          : 0;
      const cpiData = calculateCPI(task);
      const costs = cpiData.costs;

      csv += `"${task.masterSubTask?.name || "Unknown"}",`;
      csv += `"${task.categoryName || "Unknown"}",`;
      csv += `${quantityProgress.toFixed(1)}%,`;
      csv += `${cpiData.cpi?.toFixed(2) || "N/A"},`;
      csv += `${costs.budgetedCost.toFixed(2)},`;
      csv += `${costs.consumedCost.toFixed(2)},`;
      csv += `${costs.earnedValue.toFixed(2)},`;
      csv += `${costs.costVariance.toFixed(2)},`;
      csv += `${costs.remainingCost.toFixed(2)},`;
      csv += `"${costs.riskLevel}"\n`;
    });

    return csv;
  };

  // Get progress color
  const getProgressColor = (progress) => {
    if (progress >= 100) return "green";
    if (progress >= 75) return "blue";
    if (progress >= 50) return "yellow";
    if (progress >= 25) return "orange";
    return "red";
  };

  const getStatusColor = (progress) => {
    if (progress >= 100) return "green";
    if (progress > 75) return "yellow";
    if (progress > 0) return "blue";
    return "gray";
  };

  const getStatusText = (progress) => {
    if (progress >= 100) return "Complete";
    if (progress > 75) return "Near Complete";
    if (progress > 0) return "In Progress";
    return "Not Started";
  };

  const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case "l":
        return "green";
      case "m":
        return "yellow";
      case "h":
        return "red";
      default:
        return "gray";
    }
  };

  // Get unique categories for filter
  const categories = [
    ...new Set(projectTasks.map((task) => task.categoryName).filter(Boolean)),
  ];

  if (!projectTasks || projectTasks.length === 0) {
    return (
      <Alert status="info" bg={bgSecondary} borderColor={borderColor}>
        <AlertIcon />
        <VStack align="start" spacing={2}>
          <Text color={textPrimary}>
            No tasks available for progress tracking
          </Text>
          <Text fontSize="sm" color={textMuted}>
            Configure task quantities first in the Task Management tab.
          </Text>
        </VStack>
      </Alert>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Enhanced Project-Level Cost Performance Dashboard */}

      {canViewReports && (
        <Card
          bg={cardGradient}
          borderColor={borderColor}
          borderWidth="2px"
          shadow={`0 8px 32px ${shadowColor}`}
        >
          <CardHeader>
            <HStack justify="space-between" align="center">
              <HStack spacing={4}>
                <Box p={3} bg="blue.500" borderRadius="xl" shadow="lg">
                  <FiTarget size={24} color="white" />
                </Box>
                <VStack align="start" spacing={0}>
                  <HStack>
                    <Text color={textPrimary} fontSize="lg" fontWeight="bold">
                      Project Cost Performance Dashboard
                    </Text>
                    <Badge
                      colorScheme="blue"
                      variant="solid"
                      fontSize="sm"
                      px={3}
                      py={1}
                    >
                      {projectCurrency}
                    </Badge>
                    <IconButton
                      icon={colorMode === "light" ? <FiMoon /> : <FiSun />}
                      onClick={toggleColorMode}
                      variant="ghost"
                      size="sm"
                      aria-label="Toggle theme"
                      color={textSecondary}
                    />
                  </HStack>
                  <Text color={textMuted} fontSize="sm">
                    Comprehensive quantity-based cost and performance analysis
                  </Text>
                </VStack>
              </HStack>

              <Grid templateColumns="repeat(4, 1fr)" gap={6}>
                <Stat textAlign="center">
                  <StatLabel color={textMuted} fontSize="xs">
                    Project CPI
                  </StatLabel>
                  <StatNumber
                    color={`${projectStatus.color}.500`}
                    fontSize="2xl"
                  >
                    {projectCosts.projectCPI !== null &&
                    projectCosts.projectCPI !== 0
                      ? projectCosts.projectCPI.toFixed(2) <= 1
                        ? projectCosts.projectCPI.toFixed(2)
                        : "1.00"
                      : "N/A"}
                  </StatNumber>
                  <StatHelpText
                    color={`${projectStatus.color}.400`}
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {projectStatus.text}
                  </StatHelpText>
                </Stat>

                <Stat textAlign="center">
                  <StatLabel color={textMuted} fontSize="xs">
                    Budget vs Actual
                  </StatLabel>
                  <StatNumber color={textPrimary} fontSize="lg">
                    {formatCompactCurrency(projectCosts.totalBudgetedCost)}
                  </StatNumber>
                  <StatHelpText color={textMuted} fontSize="xs">
                    vs {formatCompactCurrency(projectCosts.totalConsumedCost)}
                  </StatHelpText>
                </Stat>

                <Stat textAlign="center">
                  <StatLabel color={textMuted} fontSize="xs">
                    Cost Variance
                  </StatLabel>
                  <StatNumber
                    color={
                      projectCosts.projectCostVariance >= 0
                        ? "green.500"
                        : "red.500"
                    }
                    fontSize="lg"
                  >
                    {formatCompactCurrency(
                      Math.abs(projectCosts.projectCostVariance)
                    )}
                  </StatNumber>
                  <StatHelpText color={textMuted} fontSize="xs">
                    {projectCosts.projectCostVariance >= 0 ? "Under" : "Over"}{" "}
                    budget
                  </StatHelpText>
                </Stat>

                <Stat textAlign="center">
                  <StatLabel color={textMuted} fontSize="xs">
                    Progress
                  </StatLabel>
                  <StatNumber color={textPrimary} fontSize="lg">
                    {(projectCosts.projectProgress * 100).toFixed(1)}%
                  </StatNumber>
                  <StatHelpText color={textMuted} fontSize="xs">
                    Overall completion
                  </StatHelpText>
                </Stat>
              </Grid>
            </HStack>
          </CardHeader>
        </Card>
      )}
      {/* Enhanced Header */}
      <Card bg={bgPrimary} borderColor={borderColor}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Heading size="md" color="blue.500">
                <HStack>
                  <FiActivity />
                  <Text>Progress Tracking & Cost Analysis</Text>
                  <Badge
                    colorScheme="green"
                    variant="outline"
                    fontSize="sm"
                    px={3}
                  >
                    {projectCurrency}
                  </Badge>
                </HStack>
              </Heading>
              <Text color={textMuted} fontSize="sm">
                Record detailed weekly progress with comprehensive cost
                monitoring
              </Text>
            </VStack>

            <HStack spacing={4}>
              <VStack align="end" spacing={0}>
                <Text color={textPrimary} fontSize="lg" fontWeight="bold">
                  {getMonthName(selectedMonth)}
                </Text>
                <Badge colorScheme="blue" fontSize="xs">
                  Active Period
                </Badge>
              </VStack>
            </HStack>
          </Flex>
        </CardHeader>

        <CardBody>
          {/* Enhanced Filters */}
          <Grid
            templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
            gap={4}
            mb={6}
          >
            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>
                Search Tasks
              </FormLabel>
              <Input
                placeholder="Search by name, code, activity, catego                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}"
                size="sm"
                bg={bgSecondary}
                borderColor={borderColor}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>
                Category Filter
              </FormLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                size="sm"
                bg={bgSecondary}
                borderColor={borderColor}
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>
                Options
              </FormLabel>
              <VStack spacing={2} align="start">
                <HStack>
                  <Switch
                    isChecked={showCompletedTasks}
                    onChange={(e) => setShowCompletedTasks(e.target.checked)}
                    colorScheme="blue"
                    size="sm"
                  />
                  <Text fontSize="sm" color={textSecondary}>
                    Show Completed
                  </Text>
                </HStack>
                {canViewReports && (
                  <HStack>
                    <Switch
                      isChecked={showCostDetails}
                      onChange={(e) => setShowCostDetails(e.target.checked)}
                      colorScheme="green"
                      size="sm"
                    />
                    <Text fontSize="sm" color={textSecondary}>
                      Show Costs
                    </Text>
                  </HStack>
                )}
                {canViewReports && (
                  <Button
                    leftIcon={<FiDownload />}
                    size="sm"
                    variant="outline"
                    onClick={handleExportProgress}
                  >
                    Export
                  </Button>
                )}
              </VStack>
            </FormControl>
          </Grid>
        </CardBody>
      </Card>

      {/* Enhanced Tasks by Category with Category Summaries */}
      <VStack spacing={4} align="stretch">
        {Object.entries(tasksByCategory).map(([categoryName, tasks]) => {
          const summary = calculateCategorySummary(tasks);

          return (
            <Card key={categoryName} bg={bgPrimary} borderColor={borderColor}>
              <CardHeader py={3} bg={bgSecondary}>
                <VStack spacing={3} align="stretch">
                  {/* Existing category header */}
                  <HStack justify="space-between">
                    <HStack>
                      <Text color="blue.500" fontWeight="bold" fontSize="md">
                        {categoryName}
                      </Text>
                      <Badge colorScheme="blue" variant="outline" fontSize="xs">
                        {tasks.length} tasks
                      </Badge>
                    </HStack>
                    <HStack spacing={2}>
                      <Badge variant="outline" colorScheme="blue">
                        {tasks.length} tasks
                      </Badge>
                      {showCostDetails && (
                        <Badge variant="outline" colorScheme="green">
                          {formatCompactCurrency(
                            tasks.reduce(
                              (sum, task) => sum + (task.consumedCost || 0),
                              0
                            )
                          )}{" "}
                          spent
                        </Badge>
                      )}
                    </HStack>
                  </HStack>

                  {/* NEW: Category Summary Statistics */}
                  <Grid
                    templateColumns={
                      showCostDetails ? "repeat(7, 1fr)" : "repeat(3, 1fr)"
                    }
                    gap={3}
                    pt={2}
                    borderTop="1px solid"
                    borderColor={borderColor}
                  >
                    <Stat size="sm">
                      <StatLabel color={textMuted} fontSize="xs">
                        Total Manhours
                      </StatLabel>
                      <StatNumber color={textPrimary} fontSize="md">
                        {formatNumber(summary.totalBudgetedManhours)} hrs
                      </StatNumber>
                    </Stat>

                    <Stat size="sm">
                      <StatLabel color={textMuted} fontSize="xs">
                        Consumed Manhours
                      </StatLabel>
                      <StatNumber color="blue.500" fontSize="md">
                        {formatNumber(summary.totalConsumedManhours)} hrs
                      </StatNumber>
                    </Stat>
                    <Stat size="sm">
                      <StatLabel color={textMuted} fontSize="xs">
                        Earned Manhours
                      </StatLabel>
                      <StatNumber color="blue.500" fontSize="md">
                        {formatNumber(summary.totalEarnedManhours)} hrs
                      </StatNumber>
                    </Stat>

                    {/* <Stat size="sm">
                      <StatLabel color={textMuted} fontSize="xs">Remaining Manhours</StatLabel>
                      <StatNumber
                        color={(summary.totalBudgetedManhours - summary.totalConsumedManhours) >= 0 ? 'green.500' : 'red.500'}
                        fontSize="md"
                      >
                        {formatNumber(Math.abs((summary.totalBudgetedManhours - summary.totalConsumedManhours)))} hrs
                      </StatNumber>
                      
                    </Stat> */}

                    {showCostDetails && (
                      <>
                        <Stat size="sm">
                          <StatLabel color={textMuted} fontSize="xs">
                            Budget Cost
                          </StatLabel>
                          <StatNumber color={textPrimary} fontSize="md">
                            {formatCompactCurrency(summary.totalBudgetedCost)}
                          </StatNumber>
                        </Stat>

                        <Stat size="sm">
                          <StatLabel color={textMuted} fontSize="xs">
                            Earned Cost
                          </StatLabel>
                          <StatNumber color="purple.500" fontSize="md">
                            {formatCompactCurrency(summary.totalEarnedCost)}
                          </StatNumber>
                        </Stat>

                        <Stat size="sm">
                          <StatLabel color={textMuted} fontSize="xs">
                            Consumed Cost
                          </StatLabel>
                          <StatNumber color="blue.500" fontSize="md">
                            {formatCompactCurrency(summary.totalConsumedCost)}
                          </StatNumber>
                        </Stat>

                        <Stat size="sm">
                          <StatLabel color={textMuted} fontSize="xs">
                            Cost Variance
                          </StatLabel>
                          <StatNumber
                            color={
                              summary.costVariance >= 0
                                ? "green.500"
                                : "red.500"
                            }
                            fontSize="md"
                          >
                            {formatCompactCurrency(
                              Math.abs(summary.costVariance)
                            )}
                          </StatNumber>
                          <StatHelpText fontSize="xs">
                            {summary.costVariance >= 0
                              ? "Under Budget"
                              : "Over Budget"}
                          </StatHelpText>
                        </Stat>
                      </>
                    )}
                  </Grid>
                </VStack>
              </CardHeader>

              <CardBody pt={0}>
                <Box position="relative">
                  {/* Scrollable Table Container */}
                  <Box
                    overflowX="auto"
                    overflowY="visible"
                    maxW="100%"
                    sx={{
                      "&::-webkit-scrollbar": {
                        height: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        bg: "gray.100",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        bg: "gray.400",
                        borderRadius: "4px",
                        "&:hover": {
                          bg: "gray.500",
                        },
                      },
                    }}
                  >
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th w="40px"></Th>
                          <Th minW="250px" color={textSecondary}>
                            Task Description
                          </Th>
                          <Th isNumeric minW="100px" color={textSecondary}>
                            Total Qty
                          </Th>
                          <Th isNumeric minW="100px" color={textSecondary}>
                            Productivity
                          </Th>
                          <Th isNumeric minW="100px" color={textSecondary}>
                            Installed
                          </Th>
                          <Th isNumeric minW="120px" color={textSecondary}>
                            Progress
                          </Th>
                          <Th isNumeric minW="100px" color={textSecondary}>
                            CPI
                          </Th>
                          {showCostDetails && (
                            <>
                              <Th isNumeric minW="120px" color={textSecondary}>
                                Budget
                              </Th>
                              <Th isNumeric minW="120px" color={textSecondary}>
                                Consumed
                              </Th>
                              <Th isNumeric minW="120px" color={textSecondary}>
                                Variance
                              </Th>
                              <Th
                                textAlign="center"
                                minW="100px"
                                color={textSecondary}
                              >
                                Risk
                              </Th>
                            </>
                          )}
                          <Th minW="200px" color={textSecondary}>
                            Actions
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {tasks.map((task) => {
                          const quantityProgressPercentage =
                            task.quantity > 0
                              ? ((task.totalInstalledQuantity || 0) /
                                  task.quantity) *
                                100
                              : 0;
                          const costs = calculateTaskCosts(task);

                          return (
                            <React.Fragment key={task.id}>
                              <Tr
                                ref={(el) =>
                                  (taskRowRefs.current[task.id] = el)
                                }
                                _hover={{ bg: hoverBg }}
                              >
                                <Td>
                                  <IconButton
                                    aria-label="Expand row"
                                    icon={
                                      expandedTasks[task.id] ? (
                                        <FiMinus />
                                      ) : (
                                        <FiPlus />
                                      )
                                    }
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleTaskExpansion(task.id)}
                                    color={textSecondary}
                                  />
                                </Td>
                                <Td maxW="300px">
                                  <VStack align="start" spacing={1}>
                                    <Text
                                      fontSize="sm"
                                      fontWeight="medium"
                                      noOfLines={2}
                                      color={
                                        hasRecentTargetUpdate(task.id)
                                          ? "blue.500"
                                          : textPrimary
                                      }
                                    >
                                      {task.masterSubTask?.name}
                                    </Text>
                                    <Text
                                      fontSize="xs"
                                      color={textMuted}
                                      fontFamily="mono"
                                    >
                                      {task.masterSubTask?.masterActivity?.code}
                                    </Text>
                                    {hasRecentTargetUpdate(task.id) && (
                                      <Tooltip
                                        label="Target quantity updated"
                                        placement="top"
                                      >
                                        <Badge
                                          colorScheme="blue"
                                          variant="subtle"
                                          fontSize="xs"
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          px={2}
                                          py={0.5}
                                        >
                                          <Icon as={FiTarget} w={3} h={3} />
                                          <Text></Text>
                                        </Badge>
                                      </Tooltip>
                                    )}
                                  </VStack>
                                </Td>

                                <Td isNumeric>
                                  <Text color={textPrimary}>
                                    {formatNumber(task.quantity)} {task.unit}
                                  </Text>
                                </Td>
                                <Td isNumeric>
                                  <Text color={textPrimary}>
                                    {formatNumber(task.productivity)}
                                  </Text>
                                </Td>
                                <Td isNumeric>
                                  <Text color="blue.500" fontWeight="medium">
                                    {formatNumber(
                                      task.totalInstalledQuantity || 0
                                    )}
                                  </Text>
                                </Td>
                                <Td isNumeric>
                                  <VStack spacing={1}>
                                    <Progress
                                      value={quantityProgressPercentage}
                                      colorScheme={getProgressColor(
                                        quantityProgressPercentage
                                      )}
                                      size="sm"
                                      w="60px"
                                    />
                                    <Text
                                      fontSize="xs"
                                      fontWeight="bold"
                                      color={textPrimary}
                                    >
                                      {quantityProgressPercentage.toFixed(1)}%
                                    </Text>
                                  </VStack>
                                </Td>
                                <Td isNumeric>
                                  <CPIBadge task={task} />
                                </Td>
                                {showCostDetails && (
                                  <>
                                    <Td isNumeric>
                                      <Text
                                        fontSize="xs"
                                        fontWeight="medium"
                                        color={textPrimary}
                                      >
                                        {formatCompactCurrency(
                                          costs.budgetedCost
                                        )}
                                      </Text>
                                    </Td>
                                    <Td isNumeric>
                                      <Text
                                        fontSize="xs"
                                        fontWeight="medium"
                                        color={
                                          costs.consumedCost >
                                          costs.budgetedCost
                                            ? "red.500"
                                            : "blue.500"
                                        }
                                      >
                                        {formatCompactCurrency(
                                          costs.consumedCost
                                        )}
                                      </Text>
                                    </Td>
                                    <Td isNumeric>
                                      <HStack justify="center">
                                        {costs.costVariance >= 0 ? (
                                          <FiTrendingUp
                                            color="green"
                                            size={12}
                                          />
                                        ) : (
                                          <FiTrendingDown
                                            color="red"
                                            size={12}
                                          />
                                        )}
                                        <Text
                                          fontSize="xs"
                                          fontWeight="bold"
                                          color={
                                            costs.costVariance >= 0
                                              ? "green.500"
                                              : "red.500"
                                          }
                                        >
                                          {formatCompactCurrency(
                                            Math.abs(costs.costVariance)
                                          )}
                                        </Text>
                                      </HStack>
                                    </Td>
                                    <Td textAlign="center">
                                      <Badge
                                        colorScheme={getRiskColor(
                                          costs.riskLevel
                                        )}
                                        size="sm"
                                        variant="subtle"
                                      >
                                        <HStack spacing={1}>
                                          {costs.riskLevel === "High" ? (
                                            <FiAlertCircle size={10} />
                                          ) : costs.riskLevel === "Medium" ? (
                                            <FiClock size={10} />
                                          ) : (
                                            <FiCheckCircle size={10} />
                                          )}
                                          <Text fontSize="xs">
                                            {costs.riskLevel}
                                          </Text>
                                        </HStack>
                                      </Badge>
                                    </Td>
                                  </>
                                )}
                                <Td>
                                  <HStack spacing={2}>
                                    <Button
                                      size="sm"
                                      fontSize="10px"
                                      colorScheme="blue"
                                      onClick={() =>
                                        openWeeklyProgressForm(task)
                                      }
                                    >
                                      Weekly Entry
                                    </Button>
                                    <Tooltip label="View progress history">
                                      <IconButton
                                        size="sm"
                                        icon={<FiEye />}
                                        variant="outline"
                                        onClick={() => viewTaskHistory(task)}
                                        aria-label="View history"
                                      />
                                    </Tooltip>
                                  </HStack>
                                </Td>
                              </Tr>

                              {expandedTasks[task.id] && (
                                <Tr bg={bgSecondary}>
                                  <Td colSpan={showCostDetails ? 12 : 8}>
                                    <Grid
                                      templateColumns="repeat(3, 1fr)"
                                      gap={6}
                                      p={4}
                                    >
                                      <Box>
                                        <Text
                                          color={textSecondary}
                                          fontSize="sm"
                                          fontWeight="bold"
                                          mb={3}
                                        >
                                          Progress Details
                                        </Text>
                                        <VStack align="start" spacing={2}>
                                          <HStack>
                                            <Text
                                              color={textMuted}
                                              fontSize="sm"
                                            >
                                              Installed:
                                            </Text>
                                            <Text
                                              fontWeight="medium"
                                              fontSize="sm"
                                              color={textPrimary}
                                            >
                                              {formatNumber(
                                                task.totalInstalledQuantity || 0
                                              )}{" "}
                                              {task.unit}
                                            </Text>
                                          </HStack>
                                          <HStack>
                                            <Text
                                              color={textMuted}
                                              fontSize="sm"
                                            >
                                              Earned Hours:
                                            </Text>
                                            <Text
                                              fontWeight="medium"
                                              fontSize="sm"
                                              color={textPrimary}
                                            >
                                              {formatNumber(
                                                task.totalInstalledQuantity *
                                                  task.productivity
                                              )}{" "}
                                              hrs
                                            </Text>
                                          </HStack>
                                          <HStack>
                                            <Text
                                              color={textMuted}
                                              fontSize="sm"
                                            >
                                              Consumed Hours:
                                            </Text>
                                            <Text
                                              fontWeight="medium"
                                              fontSize="sm"
                                              color={textPrimary}
                                            >
                                              {formatNumber(
                                                task.totalConsumedManhours
                                              )}{" "}
                                              hrs
                                            </Text>
                                          </HStack>
                                          <HStack>
                                            <Text
                                              color={textMuted}
                                              fontSize="sm"
                                            >
                                              Status:
                                            </Text>
                                            <Badge
                                              colorScheme={getStatusColor(
                                                quantityProgressPercentage
                                              )}
                                              variant="subtle"
                                            >
                                              {getStatusText(
                                                quantityProgressPercentage
                                              )}
                                            </Badge>
                                          </HStack>
                                        </VStack>
                                      </Box>

                                      {showCostDetails && (
                                        <>
                                          <Box>
                                            <Text
                                              color={textSecondary}
                                              fontSize="sm"
                                              fontWeight="bold"
                                              mb={3}
                                            >
                                              Cost Analysis
                                            </Text>
                                            <VStack align="start" spacing={2}>
                                              <HStack>
                                                <Text
                                                  color={textMuted}
                                                  fontSize="sm"
                                                >
                                                  Budgeted:
                                                </Text>
                                                <Text
                                                  fontWeight="medium"
                                                  fontSize="sm"
                                                  color={textPrimary}
                                                >
                                                  {formatCurrency(
                                                    costs.budgetedCost
                                                  )}
                                                </Text>
                                              </HStack>
                                              <HStack>
                                                <Text
                                                  color={textMuted}
                                                  fontSize="sm"
                                                >
                                                  Consumed:
                                                </Text>
                                                <Text
                                                  fontWeight="medium"
                                                  fontSize="sm"
                                                  color={
                                                    costs.consumedCost >
                                                    costs.budgetedCost
                                                      ? "red.500"
                                                      : "blue.500"
                                                  }
                                                >
                                                  {formatCurrency(
                                                    costs.consumedCost
                                                  )}
                                                </Text>
                                              </HStack>
                                              <HStack>
                                                <Text
                                                  color={textMuted}
                                                  fontSize="sm"
                                                >
                                                  Remaining:
                                                </Text>
                                                <Text
                                                  fontWeight="medium"
                                                  fontSize="sm"
                                                  color={
                                                    costs.remainingCost >= 0
                                                      ? "green.500"
                                                      : "red.500"
                                                  }
                                                >
                                                  {formatCurrency(
                                                    costs.remainingCost
                                                  )}
                                                </Text>
                                              </HStack>
                                              <HStack>
                                                <Text
                                                  color={textMuted}
                                                  fontSize="sm"
                                                >
                                                  Hourly Rate:
                                                </Text>
                                                <Text
                                                  fontWeight="medium"
                                                  fontSize="sm"
                                                  color={textPrimary}
                                                >
                                                  {formatCurrency(
                                                    task.hourlyRate || 0
                                                  )}
                                                  /hr
                                                </Text>
                                              </HStack>
                                            </VStack>
                                          </Box>

                                          <Box>
                                            <Text
                                              color={textSecondary}
                                              fontSize="sm"
                                              fontWeight="bold"
                                              mb={3}
                                            >
                                              Performance Metrics
                                            </Text>
                                            <VStack align="start" spacing={2}>
                                              <HStack>
                                                <Text
                                                  color={textMuted}
                                                  fontSize="sm"
                                                >
                                                  Earned Value:
                                                </Text>
                                                <Text
                                                  fontWeight="medium"
                                                  fontSize="sm"
                                                  color="green.500"
                                                >
                                                  {formatCurrency(
                                                    costs.earnedValue
                                                  )}
                                                </Text>
                                              </HStack>
                                              <HStack>
                                                <Text
                                                  color={textMuted}
                                                  fontSize="sm"
                                                >
                                                  Cost Efficiency:
                                                </Text>
                                                <Text
                                                  fontWeight="medium"
                                                  fontSize="sm"
                                                  color={textPrimary}
                                                >
                                                  {costs.costEfficiency.toFixed(
                                                    1
                                                  )}
                                                  %
                                                </Text>
                                              </HStack>
                                              <HStack>
                                                <Text
                                                  color={textMuted}
                                                  fontSize="sm"
                                                >
                                                  Projected Total:
                                                </Text>
                                                <Text
                                                  fontWeight="medium"
                                                  fontSize="sm"
                                                  color={
                                                    costs.projectedOverrun > 0
                                                      ? "red.500"
                                                      : "green.500"
                                                  }
                                                >
                                                  {formatCurrency(
                                                    costs.projectedTotalCost
                                                  )}
                                                </Text>
                                              </HStack>
                                              <HStack>
                                                <Text
                                                  color={textMuted}
                                                  fontSize="sm"
                                                >
                                                  Risk Level:
                                                </Text>
                                                <Badge
                                                  colorScheme={getRiskColor(
                                                    costs.riskLevel
                                                  )}
                                                >
                                                  {costs.riskLevel}
                                                </Badge>
                                              </HStack>
                                            </VStack>
                                          </Box>
                                        </>
                                      )}
                                    </Grid>
                                  </Td>
                                </Tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              </CardBody>
            </Card>
          );
        })}
      </VStack>

      {/* Weekly Progress Modal */}
      <Modal
        isOpen={isWeeklyProgressOpen}
        onClose={onWeeklyProgressClose}
        size="full"
        scrollBehavior="inside"
      >
        <ModalOverlay bg={overlayBg} backdropFilter="blur(10px)" />
        <ModalContent
          maxW="95vw"
          maxH="95vh"
          bg="transparent"
          border="none"
          shadow="none"
          m={4}
        >
          <Box
            bg={gradientBg}
            borderRadius="3xl"
            border="1px solid"
            borderColor="rgba(255,255,255,0.1)"
            shadow="2xl"
            overflow="hidden"
            position="relative"
          >
            <ModalHeader
              bg={cardGradient}
              borderBottom="1px solid"
              borderBottomColor={modalBorderColor}
              py={6}
              px={8}
            >
              <Flex align="center" justify="space-between">
                <HStack spacing={4}>
                  <Box bg="blue.500" p={3} borderRadius="xl" shadow="lg">
                    <FiBarChart2 size="24" color="white" />
                  </Box>
                  <VStack align="start" spacing={1}>
                    <Heading size="lg" color={textPrimary} fontWeight="700">
                      Weekly Progress & Cost Entry
                    </Heading>
                    <HStack>
                      <Text color={textMuted} fontSize="sm">
                        Track performance metrics with cost analysis
                      </Text>
                      <Badge colorScheme="blue" variant="solid" fontSize="sm">
                        {projectCurrency}
                      </Badge>
                      <Badge colorScheme="green" variant="solid" fontSize="sm">
                        Productivity : {selectedTask?.productivity ?? "NA"}
                      </Badge>
                      <Badge colorScheme="blue" variant="solid" fontSize="sm">
                        Hourly Rate : {selectedTask?.hourlyRate ?? "NA"}
                      </Badge>
                    </HStack>
                  </VStack>
                </HStack>

                {selectedTask && (
                  <HStack spacing={3}>
                    <Badge
                      colorScheme="blue"
                      variant="subtle"
                      px={3}
                      py={2}
                      borderRadius="full"
                      fontSize="sm"
                    >
                      <FiTarget style={{ marginRight: "8px" }} size={12} />
                      {selectedTask.categoryName}
                    </Badge>
                    <Badge
                      colorScheme="green"
                      variant="subtle"
                      px={3}
                      py={2}
                      borderRadius="full"
                      fontSize="sm"
                    >
                      <FiActivity style={{ marginRight: "8px" }} size={12} />
                      {selectedTask.masterSubTask?.name}
                    </Badge>
                  </HStack>
                )}
              </Flex>
            </ModalHeader>

            <IconButton
              aria-label="Close modal"
              icon={<FiX size="20" />}
              onClick={onWeeklyProgressClose}
              position="absolute"
              top={6}
              right={6}
              bg={modalCloseBg}
              color={textSecondary}
              _hover={{
                bg: modalCloseHoverBg,
                color: textPrimary,
                transform: "scale(1.1)",
              }}
              _active={{
                bg: modalCloseActiveBg,
              }}
              borderRadius="full"
              size="lg"
              zIndex={10}
              transition="all 0.2s"
              backdropFilter="blur(10px)"
              border="1px solid"
              borderColor={borderColor}
            />

            <ModalBody
              p={0}
              bg={bgPrimary}
              maxH="calc(95vh - 120px)"
              overflowY="auto"
              css={{
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: scrollTrackBg,
                  borderRadius: "10px",
                  margin: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: scrollThumbBg,
                  borderRadius: "10px",
                  border: "2px solid transparent",
                  backgroundClip: "content-box",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: scrollThumbHoverBg,
                },
              }}
            >
              {selectedTask && (
                <Box p={6}>
                  <WeeklyProgressForm
                    taskId={selectedTask.id}
                    taskDetails={{
                      ...selectedTask,
                      category: selectedTask.categoryName,
                      name: selectedTask.masterSubTask?.name,
                      currency: projectCurrency,
                    }}
                    existingProgress={selectedProgress}
                    onSave={handleWeeklyProgressSave}
                    onCancel={onWeeklyProgressClose}
                  />
                </Box>
              )}
            </ModalBody>
          </Box>
        </ModalContent>
      </Modal>

      {/* Progress History Modal */}
      <Modal isOpen={isHistoryOpen} onClose={onHistoryClose} size="4xl">
        <ModalOverlay bg={overlayBg} />
        <ModalContent bg={bgPrimary} borderColor={borderColor}>
          <ModalHeader color={textPrimary}>
            <HStack>
              <FiClock />
              <Text>
                Progress History - {selectedTask?.masterSubTask?.name}
              </Text>
              <Badge colorScheme="blue" variant="outline" fontSize="xs">
                {projectCurrency}
              </Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={textSecondary} />
          <ModalBody>
            {loadingHistory ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" color="blue.500" />
              </Flex>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg={bgSecondary}>
                    <Tr>
                      <Th color={textSecondary}>Period</Th>
                      <Th isNumeric color={textSecondary}>
                        Targeted
                      </Th>
                      <Th isNumeric color={textSecondary}>
                        Achieved
                      </Th>
                      <Th isNumeric color={textSecondary}>
                        Manhours
                      </Th>
                      <Th isNumeric minW="120px" color={textSecondary}>
                        Variance
                      </Th>
                      <Th color={textSecondary}>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(() => {
                      const taskHistory =
                        progressHistory[selectedTask?.id] || [];
                      const safeHistory = Array.isArray(taskHistory)
                        ? taskHistory
                        : [];

                      if (safeHistory.length === 0) {
                        return (
                          <Tr>
                            <Td colSpan={6}>
                              <VStack py={8} spacing={3}>
                                <Text color={textMuted} textAlign="center">
                                  No progress history found for this task
                                </Text>
                                <Text
                                  color={textMuted}
                                  fontSize="sm"
                                  textAlign="center"
                                >
                                  Add some weekly progress entries to see
                                  history here
                                </Text>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => {
                                    onHistoryClose();
                                    openWeeklyProgressForm(selectedTask);
                                  }}
                                >
                                  Add Progress Entry
                                </Button>
                              </VStack>
                            </Td>
                          </Tr>
                        );
                      }

                      return safeHistory.map((progress, index) => (
                        <Tr key={progress.id || index} _hover={{ bg: hoverBg }}>
                          <Td>
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="medium" color={textPrimary}>
                                {progress.monthName ||
                                  `${progress.month}/${progress.year}`}
                              </Text>
                              <Text fontSize="xs" color={textMuted}>
                                {progress.year} - Month {progress.month}
                              </Text>
                            </VStack>
                          </Td>
                          <Td isNumeric>
                            <Text color={textPrimary}>
                              {(progress.targetedQuantity || 0).toFixed(3)}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text color={textPrimary}>
                              {(progress.achievedQuantity || 0).toFixed(3)}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text color={textPrimary}>
                              {(progress.consumedManhours || 0).toFixed(2)}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Badge
                              colorScheme={
                                (progress.varianceQuantity || 0) >= 0
                                  ? "green"
                                  : "red"
                              }
                              fontSize="xs"
                            >
                              {(progress.varianceQuantity || 0) >= 0 ? "+" : ""}
                              {(progress.varianceQuantity || 0).toFixed(3)}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme="blue" variant="subtle">
                              {progress.status || "Completed"}
                            </Badge>
                          </Td>
                        </Tr>
                      ));
                    })()}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ProgressTracker;

